export const TAX_CALCULATOR_DEFAULTS = {
  region: 'new-york',
  regionName: 'New York',
  regionCode: 'NY',
  salary: 60000,
  rate: 'annually'
} as const
export const VALID_PAY_PERIODS = ['annually', 'monthly', 'biweekly', 'weekly', 'daily', 'hourly'] as const
export type PayPeriod = typeof VALID_PAY_PERIODS[number]
import { regionSlugToCode, regionCodeToSlug, isValidRegionSlug } from './regionSlugs'

// Country slug mappings for tax calculator URLs
// Maps country code to URL-friendly slug
const COUNTRY_CODE_TO_SLUG: Record<string, string> = {
  US: 'usa',
  GB: 'uk',
  DE: 'germany',
  IN: 'india',
  CA: 'canada'
}

// Reverse mapping: URL slug to country code
const SLUG_TO_COUNTRY_CODE: Record<string, string> = {
  usa: 'US',
  uk: 'GB',
  germany: 'DE',
  india: 'IN',
  canada: 'CA'
}

// Countries that use region-based URLs (state/province in URL)
const REGION_BASED_TAX_COUNTRIES = ['US', 'GB']

// All valid tax calculator country slugs
export const VALID_TAX_COUNTRY_SLUGS = Object.keys(SLUG_TO_COUNTRY_CODE)

export function countryCodeToTaxSlug(countryCode: string): string | null {
  return COUNTRY_CODE_TO_SLUG[countryCode] || null
}

export function taxSlugToCountryCode(slug: string): string | null {
  return SLUG_TO_COUNTRY_CODE[slug.toLowerCase()] || null
}

export function isValidTaxCountrySlug(slug: string): boolean {
  return slug.toLowerCase() in SLUG_TO_COUNTRY_CODE
}

// Legacy functions for backward compatibility with old non-region country slugs
const LEGACY_COUNTRY_SLUGS: Record<string, string> = {
  DE: 'germany',
  IN: 'india'
}

const LEGACY_SLUG_TO_COUNTRY: Record<string, string> = {
  germany: 'DE',
  india: 'IN'
}

export function countryCodeToSlug(countryCode: string): string | null {
  return LEGACY_COUNTRY_SLUGS[countryCode] || null
}

export function slugToCountryCode(slug: string): string | null {
  return LEGACY_SLUG_TO_COUNTRY[slug.toLowerCase()] || null
}

export function isValidCountrySlug(slug: string): boolean {
  return slug.toLowerCase() in LEGACY_SLUG_TO_COUNTRY
}

export function isRegionBasedCountry(countryCode: string): boolean {
  return REGION_BASED_TAX_COUNTRIES.includes(countryCode)
}
export function parseSalary(salaryParam: string | undefined | null): number {
  if (!salaryParam) return TAX_CALCULATOR_DEFAULTS.salary
  const parsed = parseInt(salaryParam, 10)
  if (isNaN(parsed) || parsed <= 0 || parsed > 100000000) {
    return TAX_CALCULATOR_DEFAULTS.salary
  }
  return parsed
}
export function parsePayPeriod(rateParam: string | undefined | null): PayPeriod {
  if (!rateParam) return TAX_CALCULATOR_DEFAULTS.rate
  const normalized = rateParam.toLowerCase()
  if (VALID_PAY_PERIODS.includes(normalized as PayPeriod)) {
    return normalized as PayPeriod
  }
  return TAX_CALCULATOR_DEFAULTS.rate
}
/**
 * Parse the combined rate-salary parameter (e.g., "annually-60000")
 */
export function parseRateSalary(param: string | undefined | null): { rate: PayPeriod; salary: number } {
  if (!param) {
    return { rate: TAX_CALCULATOR_DEFAULTS.rate, salary: TAX_CALCULATOR_DEFAULTS.salary }
  }
  const dashIndex = param.indexOf('-')
  if (dashIndex === -1) {
    // Try to parse as salary only (backwards compatibility)
    const salary = parseInt(param, 10)
    if (!isNaN(salary) && salary > 0) {
      return { rate: TAX_CALCULATOR_DEFAULTS.rate, salary }
    }
    return { rate: TAX_CALCULATOR_DEFAULTS.rate, salary: TAX_CALCULATOR_DEFAULTS.salary }
  }
  const ratePart = param.substring(0, dashIndex).toLowerCase()
  const salaryPart = param.substring(dashIndex + 1)
  const rate = VALID_PAY_PERIODS.includes(ratePart as PayPeriod)
    ? (ratePart as PayPeriod)
    : TAX_CALCULATOR_DEFAULTS.rate
  const salary = parseInt(salaryPart, 10)
  const validSalary = !isNaN(salary) && salary > 0 && salary <= 100000000
    ? salary
    : TAX_CALCULATOR_DEFAULTS.salary
  return { rate, salary: validSalary }
}
/**
 * Validate if a URL segment is a valid rate-salary format (e.g., "annually-60000")
 */
export function isValidRateSalaryFormat(segment: string): boolean {
  const dashIndex = segment.indexOf('-')
  if (dashIndex === -1) {
    return false
  }
  const ratePart = segment.substring(0, dashIndex).toLowerCase()
  const salaryPart = segment.substring(dashIndex + 1)
  if (!VALID_PAY_PERIODS.includes(ratePart as PayPeriod)) {
    return false
  }
  const salary = parseInt(salaryPart, 10)
  return !isNaN(salary) && salary > 0 && salary <= 100000000
}

export function parseRegion(regionParam: string | undefined | null): { slug: string; code: string; name: string } {
  if (!regionParam) {
    return {
      slug: TAX_CALCULATOR_DEFAULTS.region,
      code: TAX_CALCULATOR_DEFAULTS.regionCode,
      name: TAX_CALCULATOR_DEFAULTS.regionName
    }
  }
  const normalized = regionParam.toLowerCase()
  if (isValidRegionSlug(normalized)) {
    const code = regionSlugToCode(normalized)
    const name = normalized
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
    return { slug: normalized, code, name }
  }
  return {
    slug: TAX_CALCULATOR_DEFAULTS.region,
    code: TAX_CALCULATOR_DEFAULTS.regionCode,
    name: TAX_CALCULATOR_DEFAULTS.regionName
  }
}
export function buildTaxCalculatorUrl(options: {
  region?: string
  salary?: number
  rate?: PayPeriod
  countryCode?: string
} = {}): string {
  const countryCode = options.countryCode || 'US'
  const countrySlug = countryCodeToTaxSlug(countryCode) || 'usa'
  const salary = options.salary && options.salary > 0
    ? options.salary
    : TAX_CALCULATOR_DEFAULTS.salary
  const rate = options.rate && VALID_PAY_PERIODS.includes(options.rate)
    ? options.rate
    : TAX_CALCULATOR_DEFAULTS.rate

  // For non-region-based countries (India, Germany, Canada), just country + salary
  if (!isRegionBasedCountry(countryCode)) {
    return `/tax-calculator-${countrySlug}/${rate}-${salary}`
  }

  // For region-based countries (US, GB), use region slug
  let regionSlug: string = TAX_CALCULATOR_DEFAULTS.region
  if (options.region) {
    const slugFromCode = regionCodeToSlug(options.region, countryCode)
    if (slugFromCode) {
      regionSlug = slugFromCode
    } else if (isValidRegionSlug(options.region, countryCode)) {
      regionSlug = regionCodeToSlug(regionSlugToCode(options.region, countryCode), countryCode) || TAX_CALCULATOR_DEFAULTS.region
    }
  }
  return `/tax-calculator-${countrySlug}/${regionSlug.toLowerCase()}/${rate}-${salary}`
}
/**
 * Build tax calculator URL with only non-default parameters
 * (shorter URLs when defaults are used)
 */
export function buildTaxCalculatorUrlShort(options: {
  region?: string
  salary?: number
  rate?: PayPeriod
  countryCode?: string
} = {}): string {
  const countryCode = options.countryCode || 'US'
  const countrySlug = countryCodeToTaxSlug(countryCode) || 'usa'

  let regionSlug: string = TAX_CALCULATOR_DEFAULTS.region
  if (options.region) {
    const slugFromCode = regionCodeToSlug(options.region, countryCode)
    if (slugFromCode) {
      regionSlug = slugFromCode
    } else if (isValidRegionSlug(options.region, countryCode)) {
      regionSlug = regionCodeToSlug(regionSlugToCode(options.region, countryCode), countryCode) || TAX_CALCULATOR_DEFAULTS.region
    }
  }
  const salary = options.salary && options.salary > 0
    ? options.salary
    : TAX_CALCULATOR_DEFAULTS.salary
  const rate = options.rate && VALID_PAY_PERIODS.includes(options.rate)
    ? options.rate
    : TAX_CALCULATOR_DEFAULTS.rate

  // For non-region-based countries
  if (!isRegionBasedCountry(countryCode)) {
    if (salary === TAX_CALCULATOR_DEFAULTS.salary && rate === TAX_CALCULATOR_DEFAULTS.rate) {
      return `/tax-calculator-${countrySlug}`
    }
    return `/tax-calculator-${countrySlug}/${rate}-${salary}`
  }

  // For region-based countries (US, GB)
  if (regionSlug === TAX_CALCULATOR_DEFAULTS.region &&
    salary === TAX_CALCULATOR_DEFAULTS.salary &&
    rate === TAX_CALCULATOR_DEFAULTS.rate) {
    return `/tax-calculator-${countrySlug}`
  }
  if (rate === TAX_CALCULATOR_DEFAULTS.rate && salary === TAX_CALCULATOR_DEFAULTS.salary) {
    return `/tax-calculator-${countrySlug}/${regionSlug.toLowerCase()}`
  }
  return `/tax-calculator-${countrySlug}/${regionSlug.toLowerCase()}/${rate}-${salary}`
}